import { useRouter } from 'next/router';

/**
 * A hook that retrieves path query parameters from the Next.js router. Params are guaranteed to
 * be strings or undefined.
 *
 * @param {T[]} params An array of query string parameter names.
 *
 * @returns {Record<T, string | undefined>}
 *   An object containing the query string parameter values, keyed by their names.
 *
 * @template T - The type of the query string parameters.
 */
export const useRouteParamStrings = <T extends string>(
  params: T[],
): Record<T, string | undefined> => {
  const router = useRouter();

  return Object.fromEntries(
    params.map(param => {
      const paramValue = router.query[param];
      if (typeof paramValue === 'string') {
        return [param, paramValue];
      }
      return [param, undefined];
    }),
  ) as Record<T, string | undefined>;
};
